import { useMemo } from 'react';

import { getAvailableMailingProducts, type MailingProduct, type MailingType } from '@/services/ws-post/src/main/resources/rest-api';
import type { DateTimeUnixType } from '@/js/libs/api-helpers';
import { useSubscribe } from '@/js/libs/observable';
import { useRequest } from '@/js/libs/use-request';

import { useFeatureToggleContext } from 'src/modules/FeatureToggle/index.ts';
import { getPostDtoDate } from '../helpers.ts';
import { getProductsForMailingType } from '../mailingProduct.ts';

export const useAvailableMailingProducts = (
  mailingType: MailingType | undefined,
  distributionDate?: DateTimeUnixType,
): { products: MailingProduct[]; pending: boolean; isCatalogLoaded: boolean } => {
  const { isAdditionalPromopostProductsEnabled } = useFeatureToggleContext();

  const requestParams = useMemo(() => {
    if (!isAdditionalPromopostProductsEnabled) {
      return {};
    }

    const distributionDateParam = getPostDtoDate(distributionDate);

    if (!distributionDateParam) {
      return {};
    }

    return { distributionDate: distributionDateParam };
  }, [distributionDate, isAdditionalPromopostProductsEnabled]);

  const availableMailingProductsRequest = useRequest({
    apiMethod: getAvailableMailingProducts,
    params: requestParams,
  });

  const availableProducts = useSubscribe(availableMailingProductsRequest.data$, (response) => response?.data);
  const pending = useSubscribe(availableMailingProductsRequest.pending$);

  const products = useMemo(() => getProductsForMailingType(availableProducts, mailingType), [availableProducts, mailingType]);

  return { products, pending, isCatalogLoaded: availableProducts !== undefined };
};
